"use client";

import { useState } from "react";
import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";

function Stars({ value, onPick }: { value: number; onPick: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Puanınız">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} yıldız`}
          onClick={() => onPick(n)}
          className="rounded-full p-1 transition hover:scale-110"
        >
          <svg viewBox="0 0 24 24" className={`h-7 w-7 ${n <= value ? "text-amber-400" : "text-zinc-300"}`} fill="currentColor" aria-hidden="true">
            <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

export function ReviewForm({ slug, onSubmitted }: { slug: string; onSubmitted?: () => void }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (!user) {
    return (
      <div id="yorum-yaz" className="w-full scroll-mt-32 rounded-[30px] bg-Background-light p-6 text-center outline outline-1 outline-Background">
        <p className="text-base font-semibold text-800">Yorum Yaz</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-500">Deneyimini paylaşmak için giriş yap — yorumun onay sonrası yayınlanır.</p>
        <Link href={`/hesabim/giris?callbackUrl=${encodeURIComponent(`/urunler/${slug}#yorum-yaz`)}`} className="mt-3 inline-flex rounded-full bg-Primary px-6 py-2.5 text-sm font-medium uppercase text-White transition hover:bg-Primary-dark">
          Giriş Yap
        </Link>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, rating, body }),
      });
      const data = (await res.json().catch(() => null)) as { success?: boolean; error?: string } | null;
      setLoading(false);
      if (!res.ok || !data?.success) {
        setError(data?.error ?? "Gönderilemedi. Tekrar deneyin.");
        return;
      }
      setSent(true);
      setBody("");
      onSubmitted?.();
    } catch {
      setLoading(false);
      setError("Bağlantı hatası. Tekrar deneyin.");
    }
  }

  if (sent) {
    return (
      <div id="yorum-yaz" className="w-full scroll-mt-32 rounded-[30px] bg-emerald-50 p-6 text-center outline outline-1 outline-emerald-200">
        <p className="text-base font-semibold text-emerald-900">Yorumun alındı ✓</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-emerald-800">Onay sonrası bu sayfada yayınlanacak. Teşekkürler!</p>
      </div>
    );
  }

  return (
    <form id="yorum-yaz" aria-label="Yorum yazma formu" onSubmit={handleSubmit} className="w-full scroll-mt-32 rounded-[30px] bg-Background-light p-6 outline outline-1 outline-Background">
      <p className="text-base font-semibold text-800">Yorum Yaz</p>
      <p className="mt-1 text-xs text-500">Yorumlar onay sonrası yayınlanır.</p>
      {error && <p role="alert" className="mt-3 rounded-2xl bg-Tertiary/10 p-3 text-xs font-medium text-Tertiary outline outline-1 outline-Tertiary/30">{error}</p>}
      <div className="mt-3">
        <Stars value={rating} onPick={setRating} />
      </div>
      <label htmlFor="yorum-metin" className="sr-only">Yorumun</label>
      <textarea
        id="yorum-metin"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
        minLength={10}
        maxLength={1000}
        rows={3}
        placeholder="Deneyimini paylaş... (en az 10 karakter)"
        className="mt-3 min-h-[76px] w-full resize-none rounded-2xl bg-White px-4 py-3 text-sm outline outline-1 outline-Background-dark placeholder:text-500 focus:outline-2 focus:outline-Primary"
      />
      <button type="submit" disabled={loading} className="mt-3 h-12 shrink-0 rounded-full bg-Primary px-6 text-sm font-medium uppercase text-White transition hover:bg-Primary-dark disabled:opacity-60">
        {loading ? "Gönderiliyor..." : "Gönder"}
      </button>
    </form>
  );
}
